/*
 * The day's journeys for one vehicle, drawn on the map that is already on screen and
 * listed beside it.
 *
 * IT DRAWS ON THE CALLER'S MAP RATHER THAN OWNING ONE. A second map would put the same
 * vehicle in two places and let the two disagree about where it is; the position pin and
 * the day's route belong on one surface.
 *
 * The window is a CALENDAR DAY in the operator's own timezone, not a rolling 24 hours.
 * "Yesterday" is a question a fleet manager asks; "the last 24 hours" is a question the
 * API's limit asks. The end is clamped to now because the backend refuses a future `to`.
 */

import React from 'react';
import { getStyle } from '../../utils/utils';
import { fleetLocationStyles as styles } from './fleetLocationStyles';
import { normaliseHistory, hasPosition, cachedBoundaryNames, placeNamesKey,
  rideIdentity, rideBoundarySide } from './historyData';
import VehicleHistoryCalendar, { todayValue, dayAsDate, monthValue, windowFor }
  from './VehicleHistoryCalendar';
import VehicleHistoryTimeline from './VehicleHistoryTimeline';
import { RIDE_COLOURS } from './historyPresentation';
import { ridePoints, padBounds, arrowAnchors, arrowCountFor, MARKER_BOX,
  startMarkerSvg, endMarkerSvg, arrowMarkerSvg } from './historyMapGeometry';

// How many completed days are held at once. Thirty is a month of browsing; the samples of
// a day are the bulk of it, and an unbounded map would hold every one of them.
const DAY_CACHE_LIMIT = 30;
const DAY_CACHE_TTL_MS = 5 * 60 * 1000;

class VehicleHistoryPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      day: todayValue(),
      status: 'idle',
      rides: [],
      rows: [],
      incomplete: true,
      emptyState: 'unknown',
      servedWindow: null,
      samplingSeconds: null,
      truncated: false,
      coveredFrom: null,
      message: '',
      selected: null,
      selectedNames: null,
      // day string -> 'moved' | 'still'. A day that is in no map the endpoint sent is
      // UNKNOWN, and unknown is not a value stored here: absence is what it means.
      coverage: {},
    };
    this.group = null;
    // A SLOW DAY MUST NOT PAINT OVER A FAST ONE. Two fetches are in flight the moment an
    // operator picks a second day before the first answers, and the map they draw on is
    // the one showing the live position -- a late reply would put yesterday's route under
    // today's pin. Only the newest request may land, and none may land after unmount.
    this.loadId = 0;
    this.mounted = false;
    // WHAT THE LAST WALK ALREADY PAID FOR. The response is sent `no-store` and the upstream
    // budget is six yielding requests per minute per credential, shared with the fleet map:
    // measured on the live stack, the SEVENTH history read inside a minute is refused and
    // the day comes back empty carrying a partial-day notice. Six clicks around a calendar
    // is ordinary browsing, so the cost of re-reading a day has to go somewhere.
    //
    // Only nontruncated past days are reused, for at most five minutes: late uploads can
    // correct even yesterday. This is checked on explicit reselection/revisit, not a timer;
    // an already-open day is a snapshot rather than a live stream. Today and truncated
    // responses are never kept. The separate entry cap bounds retained sample memory.
    this.dayCache = new Map();
    // THE DAY, NOT THE PIN (operator, 2026-08-30). This deliberately moves the camera off
    // the live position on the first load: a screen titled with a day whose routes are off
    // the edge of the map is showing the wrong thing, and the pin is inside the day's
    // bounds whenever the vehicle drove at all.
    this.fitOnDraw = true;
    // A SEPARATE CACHE FROM `dayCache`, DELIBERATELY. That one holds whole days of position
    // samples and evicts at thirty entries because of their weight; a month of coverage is a
    // few dozen short strings, and evicting one would re-fetch the month being looked at.
    this.coverageCache = new Map();
    // Mount and the first day change can ask for the same month before either answers.
    this.coverageInFlight = new Set();
    this.nameCache = new Map();
    this.nameGeneration = 0;
    this.namePendingKey = null;
    this.nameController = null;
  }

  componentDidMount() {
    this.mounted = true;
    this.load();
    // `onMonthChange` does not fire for the month the picker opens on.
    this.loadCoverage(monthValue(dayAsDate(this.state.day)));
  }

  componentDidUpdate(prevProps) {
    // The map arrives after the SDK and the first position do, so the first load usually
    // has nowhere to draw. Draw when it appears rather than losing the day already fetched.
    if (!prevProps.map && this.props.map) {
      this.draw(this.state.rides);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.cancelPlaceNames();
    this.clear();
  }

  onDayChange(day) {
    this.fitOnDraw = true;
    this.setState({ day }, () => this.load());
    // Picking a day in the neighbouring month scrolls the grid there without an arrow press.
    this.loadCoverage(monthValue(dayAsDate(day)));
  }

  clear() {
    if (this.group && this.props.map) {
      this.props.map.removeObject(this.group);
    }
    this.group = null;
  }

  async load() {
    const { vehicleId, t } = this.props;
    const generation = this.loadId + 1;
    this.loadId = generation;
    this.cancelPlaceNames();
    this.clear();
    const range = windowFor(this.state.day);
    if (!range.valid) {
      this.setState({
        status: 'error',
        message: t(range.malformed ? 'historyNoDay' : 'historyDayInFuture'),
        rides: [],
        rows: [], selected: null, selectedNames: null,
      });
      this.clear();
      return;
    }
    // KEYED BY VEHICLE AS WELL AS DAY. The panel does not reload when `vehicleId` changes,
    // so it is expected to be remounted per vehicle -- but a cache that assumed that would
    // serve one truck's Tuesday as another's, and be right only by luck.
    const cacheKey = this.props.vehicleId + ':' + this.state.day;
    const cached = this.dayCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      // The generation counter still advances: a slower request for another day must not
      // land on top of this one just because this one answered instantly.
      this.setState({ status: 'ok', message: '', selected: null, selectedNames: null, ...cached.day });
      this.draw(cached.day.rides);
      return;
    }
    if (cached) {
      this.dayCache.delete(cacheKey);
    }
    const current = () => this.mounted && this.loadId === generation;
    this.setState({ status: 'loading', message: '', selected: null, selectedNames: null });
    try {
      const url = '/uiapi/1/fleetlocation/vehicle/' + vehicleId + '/history/'
        + '?from=' + encodeURIComponent(range.from) + '&to=' + encodeURIComponent(range.to);
      const resp = await fetch(url, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      const data = await resp.json().catch(() => ({}));
      if (!current()) {
        return;
      }
      if (!resp.ok) {
        // The endpoint names its own refusals in `detail`; showing our own guess instead
        // would hide the difference between "this truck cannot do this" and "Scania is
        // down". A name with no translation falls back rather than printing the token.
        this.setState({
          status: 'error',
          rides: [],
          rows: [],
          message: t('historyStatus.' + (data.detail || 'unavailable'), {
            keySeparator: '.',
            defaultValue: t('historyUnavailable'),
          }),
        });
        this.clear();
        return;
      }
      // Any fresh answer can correct an arrival while keeping the window/start identity,
      // including uncached partial days. Names are still requested only on selection.
      this.nameCache.clear();
      const day = {
        ...normaliseHistory(data),
        servedWindow: data.window || { from: range.from, to: range.to },
        samplingSeconds: data.sampling_interval_seconds,
        truncated: !!data.truncated,
        coveredFrom: data.covered_from || null,
      };
      if (!day.truncated && this.state.day !== todayValue()) {
        if (this.dayCache.size >= DAY_CACHE_LIMIT) {
          this.dayCache.delete(this.dayCache.keys().next().value);
        }
        this.dayCache.set(cacheKey, { day, expiresAt: Date.now() + DAY_CACHE_TTL_MS });
      }
      this.setState({ status: 'ok', message: '', ...day });
      this.draw(day.rides);
    } catch (err) {
      if (!current()) {
        return;
      }
      this.setState({ status: 'error', rides: [], rows: [], message: t('historyUnavailable') });
      this.clear();
    }
  }

  draw(rides) {
    const { map } = this.props;
    const H = window.H;
    if (!map || !H) {
      return;
    }
    this.clear();
    const group = new H.map.Group();
    let drewSomething = false;
    rides.forEach((ride, index) => {
      const points = ridePoints(ride);
      if (points.length === 0) {
        return;
      }
      const colour = RIDE_COLOURS[index % RIDE_COLOURS.length];
      if (points.length > 1) {
        const line = new H.geo.LineString();
        points.forEach((p) => line.pushPoint(p));
        group.addObject(
          new H.map.Polyline(line, { style: { lineWidth: 5, strokeColor: colour } })
        );
      }
      // ENDS, NOT EVERY SAMPLE. A ten-minute sampling interval means the dots between the
      // ends are arbitrary points on a road, and marking them invites reading them as stops.
      // The arrows are not dots: they carry the heading of the leg they sit on and say
      // nothing about what happened there.
      if (points.length > 1) {
        arrowAnchors(points, arrowCountFor(this.props.screenSize)).forEach((anchor) => {
          group.addObject(this.iconMarker(
            { lat: anchor.lat, lng: anchor.lng },
            arrowMarkerSvg(colour, anchor.bearing)
          ));
        });
      }
      group.addObject(this.iconMarker(points[0], startMarkerSvg(colour)));
      if (points.length > 1) {
        group.addObject(this.iconMarker(points[points.length - 1], endMarkerSvg(colour)));
      }
      drewSomething = true;
    });
    if (!drewSomething) {
      return;
    }
    map.addObject(group);
    this.group = group;
    if (this.fitOnDraw) {
      this.fitOnDraw = false;
      this.fitTo(group.getBoundingBox());
    }
  }

  fitTo(rect) {
    const H = window.H;
    const box = padBounds({
      top: rect.getTop(),
      left: rect.getLeft(),
      bottom: rect.getBottom(),
      right: rect.getRight(),
    });
    this.props.map.getViewModel().setLookAtData({
      bounds: new H.geo.Rect(box.top, box.left, box.bottom, box.right),
    });
  }

  iconMarker(point, svg) {
    const H = window.H;
    return new H.map.Marker(point, {
      icon: new H.map.Icon(svg, {
        size: MARKER_BOX,
        anchor: { x: MARKER_BOX.w / 2, y: MARKER_BOX.h / 2 },
      }),
    });
  }

  // QUIETLY OPTIONAL, BY REQUIREMENT. The coverage endpoint may not be deployed yet, and an
  // organisation may hold no stored history at all. Neither is something the operator can
  // act on, and an unmarked picker is exactly the picker that worked yesterday -- so nothing
  // in here touches `status` or `message`, and a failed month is remembered as empty rather
  // than retried on every render.
  async loadCoverage(month) {
    const key = this.props.vehicleId + ':' + month;
    if (this.coverageCache.has(key) || this.coverageInFlight.has(key)) {
      return;
    }
    this.coverageInFlight.add(key);
    let held = {};
    try {
      const resp = await fetch(
        '/uiapi/1/fleetlocation/vehicle/' + this.props.vehicleId + '/history/coverage/'
          + '?month=' + encodeURIComponent(month),
        { credentials: 'include', headers: { Accept: 'application/json' } }
      );
      const data = resp.ok ? await resp.json().catch(() => ({})) : {};
      // A 200 CARRYING NO `days` IS AN EMPTY MONTH, NOT A CRASH. An older build of the
      // endpoint, a proxy's own answer, or a stub in another suite all look like this.
      if (data && data.days && typeof data.days === 'object') {
        held = data.days;
      }
    } catch (err) {
      held = {};
    }
    this.coverageCache.set(key, held);
    this.coverageInFlight.delete(key);
    if (this.mounted) {
      this.setState((prev) => ({ coverage: { ...prev.coverage, ...held } }));
    }
  }

  cancelPlaceNames() {
    this.nameGeneration += 1;
    this.namePendingKey = null;
    if (this.nameController) {
      this.nameController.abort();
      this.nameController = null;
    }
  }

  async loadPlaceNames(ride) {
    const { vehicleId } = this.props;
    const window = this.state.servedWindow;
    const key = placeNamesKey(vehicleId, window, ride);
    const initial = cachedBoundaryNames(ride);
    if (key && key === this.namePendingKey) { return; }
    this.cancelPlaceNames();
    const held = key && this.nameCache.get(key);
    if (held && held.expiresAt > Date.now()) {
      this.setState({ selectedNames: held.names });
      return;
    }
    const needsLookup = ['start', 'end'].some((side) =>
      hasPosition(ride[side]) && !initial[side]
    );
    if (!key || !needsLookup) {
      this.setState({ selectedNames: initial });
      return;
    }
    const generation = this.nameGeneration;
    const current = () => this.mounted && generation === this.nameGeneration;
    this.namePendingKey = key;
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();
    this.nameController = controller;
    let names = initial;
    let succeeded = false;
    try {
      const response = await fetch(
        '/uiapi/1/fleetlocation/vehicle/' + vehicleId + '/history/place-names/'
        + '?from=' + encodeURIComponent(window.from)
        + '&to=' + encodeURIComponent(window.to)
        + '&ride=' + encodeURIComponent(rideIdentity(ride))
        + '&side=' + rideBoundarySide(ride),
        {
          credentials: 'include', headers: { Accept: 'application/json' },
          ...(controller ? { signal: controller.signal } : {}),
        }
      );
      const data = response.ok ? await response.json() : {};
      succeeded = response.ok;
      if (response.ok) {
        names = {};
        ['start', 'end'].forEach((side) => {
          names[side] = data[side] && typeof data[side].place_name === 'string'
            ? data[side].place_name : null;
        });
      }
    } catch (error) {
      // A name is optional. The displayed coordinate survives rejection, timeout and 404.
      succeeded = false;
    }
    if (!current()) { return; }
    this.namePendingKey = null;
    this.nameController = null;
    if (this.nameCache.size >= 60) {
      this.nameCache.delete(this.nameCache.keys().next().value);
    }
    // Successful names last for this bounded panel session. Missing names/errors get a short
    // cooldown, so repeatedly clicking an unresolved depot cannot create a request storm.
    const resolved = ['start', 'end'].every((side) => !hasPosition(ride[side]) || names[side]);
    this.nameCache.set(key, {
      names, expiresAt: succeeded && resolved ? Infinity : Date.now() + 30000,
    });
    this.setState({ selectedNames: names });
  }

  focus(ride) {
    if (this.state.selected === ride) {
      this.cancelPlaceNames();
      this.setState({ selected: null, selectedNames: null });
      if (this.group && this.props.map && window.H) {
        this.fitTo(this.group.getBoundingBox());
      }
      return;
    }
    this.setState({ selected: ride, selectedNames: cachedBoundaryNames(ride) });
    this.loadPlaceNames(ride);
    const { map } = this.props;
    const H = window.H;
    const points = ridePoints(ride);
    if (!map || !H || points.length === 0) {
      return;
    }
    if (points.length === 1) {
      map.getViewModel().setLookAtData({ position: points[0], zoom: 15 });
      return;
    }
    const line = new H.geo.LineString();
    points.forEach((p) => line.pushPoint(p));
    this.fitTo(line.getBoundingBox());
  }

  render() {
    const { t, screenSize, retentionMonths } = this.props;
    const { day, coverage } = this.state;
    const language = this.props.i18n && this.props.i18n.language;
    return (
      <div>
        <h3 style={getStyle(screenSize, styles, 'subheading')}>{t('historyTitle')}</h3>
        {/* The calendar stays open: selecting a day is the primary control. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 24 }}>
          <VehicleHistoryCalendar day={day} coverage={coverage} retentionMonths={retentionMonths}
            language={language} t={t} screenSize={screenSize}
            onDayChange={(picked) => this.onDayChange(picked)}
            onMonthChange={(month) => this.loadCoverage(month)} />
          <VehicleHistoryTimeline history={this.state} t={t} language={language}
            screenSize={screenSize} clampedAt={windowFor(day).clampedAt}
            onSelectRide={(ride) => this.focus(ride)} />
        </div>
      </div>
    );
  }
}

export { VehicleHistoryPanel };
// Preserve the existing public helper imports as identical aliases, not copied logic.
export { retentionCutoff, monthValue, PICKER_THEME, windowFor } from './VehicleHistoryCalendar';
export { bearingDegrees, arrowAnchors, arrowCountFor, startMarkerSvg, endMarkerSvg,
  arrowMarkerSvg, padBounds, ridePoints } from './historyMapGeometry';
export { rideKey, formatDistance, formatDuration, rideStats, dayTotals } from './historyPresentation';
export default VehicleHistoryPanel;
